import React from 'react';
import { Link } from 'react-router-dom';
import { Cookie, ArrowLeft } from 'lucide-react';

const CookiePolicy = () => {
  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <Link
          to="/events"
          className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
        >
          <ArrowLeft size={16} className="mr-2" />
          Back to Events
        </Link>
      </div>

      <div className="card">
        <div className="flex items-center space-x-3 mb-6">
          <Cookie className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Cookie Policy</h1>
        </div>

        <div className="prose prose-gray dark:prose-invert max-w-none">
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            <strong>Last updated:</strong> {new Date().toLocaleDateString()}
          </p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Introduction</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              This Cookie Policy explains how the Blood on the Clocktower Event Signup Application ("we," "our," or "us") 
              uses cookies and similar technologies when you visit our application.
            </p>
            <p className="text-gray-700 dark:text-gray-300">
              We are committed to being transparent about our data practices and protecting your privacy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">What Are Cookies?</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Cookies are small text files that are stored on your device (computer, tablet, or mobile phone) when you visit a website. 
              They help websites remember information about your visit, such as your preferred language and other settings.
            </p>
            <p className="text-gray-700 dark:text-gray-300">
              Cookies can make your next visit easier and the site more useful to you.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Our Cookie Usage</h2>
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-3">
                🍪 We Do Not Use Cookies
              </h3>
              <p className="text-blue-800 dark:text-blue-200">
                The Blood on the Clocktower Event Signup Application is designed with privacy in mind and does not use 
                traditional cookies for tracking or storing user information. We rely on secure session management 
                and Discord OAuth for user authentication instead.
              </p>
            </div>
            
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Instead of cookies, our application uses:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300">
              <li><strong>Session Storage:</strong> Temporary data stored in your browser for the duration of your session</li>
              <li><strong>Local Storage:</strong> Persistent data stored locally on your device for user preferences</li>
              <li><strong>Discord OAuth:</strong> Secure authentication through Discord's OAuth 2.0 system</li>
              <li><strong>Server-Side Sessions:</strong> Secure session management on our servers</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Session and Local Storage</h2>
            <div className="space-y-4 text-gray-700 dark:text-gray-300">
              <p>
                Our application may use browser storage technologies for the following purposes:
              </p>
              <ul className="list-disc pl-6">
                <li><strong>Authentication Tokens:</strong> Storing your Discord authentication token securely</li>
                <li><strong>User Preferences:</strong> Remembering your theme preferences (light/dark mode)</li>
                <li><strong>Application State:</strong> Maintaining your current session and navigation state</li>
                <li><strong>Form Data:</strong> Temporarily storing form data to prevent loss during navigation</li>
              </ul>
              <p>
                This data is stored locally on your device and is not shared with third parties.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Third-Party Services</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              While we do not use cookies directly, our application integrates with several third-party services:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 dark:text-gray-300">
              <li><strong>Discord:</strong> For user authentication (may use cookies for their own purposes)</li>
              <li><strong>Firebase:</strong> For front-end hosting and deployment</li>
              <li><strong>Neon:</strong> For database storage and management</li>
              <li><strong>Render:</strong> For backend server processing</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              We have no control over Discord's cookie usage. Please refer to Discord's Privacy Policy and Cookie Policy 
              for information about how they use cookies and similar technologies.
            </p>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <p className="text-yellow-800 dark:text-yellow-200 text-sm">
                <strong>Data Location:</strong> All data is stored in the United States through our hosting providers.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Managing Your Data</h2>
            <div className="space-y-4 text-gray-700 dark:text-gray-300">
              <p>
                You can manage the data stored by our application in several ways:
              </p>
              <ul className="list-disc pl-6">
                <li><strong>Clear Browser Data:</strong> Clear your browser's local storage and session storage</li>
                <li><strong>Logout:</strong> Use the logout function to clear your authentication data</li>
                <li><strong>Browser Settings:</strong> Adjust your browser's privacy and security settings</li>
                <li><strong>Contact Us:</strong> Request deletion of your account and associated data</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Privacy-First Approach</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Our decision not to use cookies reflects our commitment to user privacy. We believe in:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300">
              <li>Minimizing data collection to only what's necessary</li>
              <li>Providing transparent information about our data practices</li>
              <li>Giving users control over their personal information</li>
              <li>Using secure, privacy-friendly technologies</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Future Changes</h2>
            <p className="text-gray-700 dark:text-gray-300">
              If we decide to implement cookies in the future, we will update this policy and provide clear notice 
              to users about any changes. We will always obtain your consent before using cookies for purposes 
              beyond essential functionality.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Contact Us</h2>
            <p className="text-gray-700 dark:text-gray-300">
              If you have any questions about this Cookie Policy or our data practices, please contact us through 
              the Discord server or through the application's support channels.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Related Policies</h2>
            <div className="space-y-2 text-gray-700 dark:text-gray-300">
              <p>For more information about how we handle your data, please also review:</p>
              <ul className="list-disc pl-6">
                <li><Link to="/privacy-policy" className="text-blue-600 dark:text-blue-400 hover:underline">Privacy Policy</Link></li>
                <li><Link to="/terms-of-use" className="text-blue-600 dark:text-blue-400 hover:underline">Terms of Use</Link></li>
              </ul>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy; 